import { X, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import ReactDOM from 'react-dom';
import { useModalFocus } from '../utils/hooks/useModalFocus';

interface AnnouncementModalProps {
  isOpen: boolean;
  announcement: {
    id: string;
    title: string;
    message: string;
    buttonText: string;
    buttonDestination: string;
  } | null;
  onClose: () => void;
  onActionClick: () => void;
  /** When set (e.g. 10001), portal is shown above admin overlay (z-[10000]) */
  portalZIndex?: number;
}

export function AnnouncementModal({ isOpen, announcement, onClose, onActionClick, portalZIndex }: AnnouncementModalProps) {
  useModalFocus(isOpen);
  if (!announcement) return null;

  return ReactDOM.createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div key="announcement-modal" className="fixed inset-0" style={{ zIndex: portalZIndex ?? 'var(--z-important)' }}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center p-4 pt-safe pb-24 pb-safe">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with gradient */}
              <div className="bg-gradient-to-br from-purple-600 via-pink-500 to-purple-700 p-6 relative">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                  aria-label="Lukk"
                >
                  <X className="w-5 h-5 text-white" />
                </button>

                <div className="flex items-center gap-3 mb-2">
                  <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">{announcement.title}</h2>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line text-center">
                  {announcement.message}
                </p>

                {/* Actions */}
                <div className="flex flex-col gap-3">
                  {announcement.buttonDestination && (
                    <button
                      onClick={onActionClick}
                      className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                    >
                      {announcement.buttonText}
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                  >
                    Lukk
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
